import { useCallback, useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './pages/Login';
import { NewRequestsTab } from './pages/NewRequestsTab';
import { StaffingTab } from './pages/StaffingTab';
import { StubTab } from './pages/StubTab';
import { getStatusCounts } from './services/api';
import type { StatusCounts } from './types/api';

type TabKey = 'new' | 'staffing' | 'kickoff' | 'active';

function Shell() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState<TabKey>('new');
  const [counts, setCounts] = useState<StatusCounts | null>(null);

  const refreshCounts = useCallback(async () => {
    if (!user) return;
    try {
      setCounts(await getStatusCounts());
    } catch {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    void refreshCounts();
  }, [refreshCounts]);

  if (loading) {
    return (
      <div className="page page-center">
        <div className="text-secondary">Loading…</div>
      </div>
    );
  }
  if (!user) return <Login />;

  const newQueueCount =
    (counts?.new ?? 0) + (counts?.under_review ?? 0);

  return (
    <div className="page">
      <header className="navbar navbar-expand-md navbar-light d-print-none">
        <div className="container-xl">
          <h1 className="navbar-brand h3 mb-0">
            NA Florida — Workgroup Management
          </h1>
          <div className="navbar-nav flex-row order-md-last">
            <div className="nav-item d-flex align-items-center text-secondary">
              <span className="me-2">{user.name}</span>
              <span className="badge bg-blue-lt">{user.role}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="page-wrapper">
        <div className="container-xl py-3">
          <ul className="nav nav-tabs mb-3" role="tablist">
            <TabButton
              active={tab === 'new'}
              label="New Workgroup Requests"
              count={newQueueCount}
              onClick={() => setTab('new')}
            />
            <TabButton
              active={tab === 'staffing'}
              label="HRP Staffing"
              count={(counts?.submitted_to_hrp ?? 0) + (counts?.staffing ?? 0)}
              onClick={() => setTab('staffing')}
            />
            <TabButton
              active={tab === 'kickoff'}
              label="Workgroup Kickoff"
              count={counts?.kickoff ?? 0}
              onClick={() => setTab('kickoff')}
            />
            <TabButton
              active={tab === 'active'}
              label="Active Workgroups"
              count={counts?.active ?? 0}
              onClick={() => setTab('active')}
            />
          </ul>

          {tab === 'new' && <NewRequestsTab onChange={refreshCounts} />}
          {tab === 'staffing' && <StaffingTab onChange={refreshCounts} />}
          {tab === 'kickoff' && (
            <StubTab
              title="Workgroup Kickoff"
              description="FD drafts a kickoff email, schedules the first Zoom meeting, and after kickoff records the recurring meeting cadence."
              itemCount={counts?.kickoff ?? 0}
            />
          )}
          {tab === 'active' && (
            <StubTab
              title="Active Workgroups"
              description="Active workgroups with shared Drive folders, changelog, and report generation."
              itemCount={counts?.active ?? 0}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <li className="nav-item">
      <button
        type="button"
        className={`nav-link ${active ? 'active' : ''}`}
        onClick={onClick}
      >
        {label}
        {count > 0 && <span className="badge bg-blue-lt ms-2">{count}</span>}
      </button>
    </li>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  );
}
